import { Link } from "react-router-dom";
import { Minus, Plus } from "lucide-react";
import type { FooterLink, FooterLinkGroup as Group } from "./useFooterLinks";

const linkClass = (link: FooterLink) =>
  `block py-2 md:py-0 transition-colors duration-200 ${
    link.emphasis ? "font-semibold text-background" : "text-background/70 hover:text-background"
  }`;

const FooterLinkItem = ({ link }: { link: FooterLink }) =>
  link.external ? (
    <a
      href={link.href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={link.onClick}
      data-testid={link.testId}
      className={linkClass(link)}
    >
      {link.title}
    </a>
  ) : (
    <Link to={link.href} data-testid={link.testId} className={linkClass(link)}>
      {link.title}
    </Link>
  );

interface FooterLinkGroupProps {
  group: Group;
  open: boolean;
  onToggle: () => void;
}

/**
 * One link group: a collapsible section on phones, a plain column from `md` up.
 * The links are always in the server-rendered HTML (hidden with CSS only), so
 * collapsing them on mobile does not remove internal links for crawlers.
 */
const FooterLinkGroup = ({ group, open, onToggle }: FooterLinkGroupProps) => {
  const listId = `footer-group-${group.id}`;
  const Icon = open ? Minus : Plus;

  return (
    <nav aria-label={group.title} className="border-b border-background/15 md:border-0">
      <h3 className="md:hidden">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={listId}
          data-testid={`footer-toggle-${group.id}`}
          className="flex min-h-[52px] w-full items-center justify-between text-left text-base font-semibold"
        >
          {group.title}
          <Icon className="h-5 w-5 text-background/70" aria-hidden="true" />
        </button>
      </h3>
      <h3 className="mb-4 hidden text-sm font-semibold uppercase tracking-wider text-background/60 md:block">
        {group.title}
      </h3>
      <ul id={listId} className={`${open ? "block" : "hidden"} pb-3 md:block md:space-y-2.5 md:pb-0`}>
        {group.links.map((link) => (
          <li key={link.href + link.title}>
            <FooterLinkItem link={link} />
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default FooterLinkGroup;
